import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../../redux/action/authAction/AuthAction";
import {
  LuMenu,
  LuSearch,
  LuBell,
  LuChevronDown,
  LuUser,
  LuLogOut,
  LuX,
} from "react-icons/lu";
import { useSidebar } from "../../hooks/SidebarContext";
import { Link } from "react-router-dom";
import { PROFILE } from "../../routes/RouteConstant";

export default function Navbar({ pageTitle = "Dashboard" }) {
  const { toggleMobile } = useSidebar();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  return (
    <header
      className="sticky top-0 z-20 flex h-16 items-center gap-3 px-4 sm:px-6"
      style={{
        background:
          "linear-gradient(90deg, #121A3D 0%, #0D1330 55%, #0B1128 100%)",
        borderBottom: "1px solid transparent",
        borderImage:
          "linear-gradient(90deg, rgba(91,124,255,0.3), rgba(91,124,255,0.03) 70%) 1",
        boxShadow: "0 4px 24px -8px rgba(0,0,0,0.4)",
      }}
    >
      <button
        type="button"
        onClick={toggleMobile}
        className="shrink-0 rounded-md p-2 text-slate-400 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white lg:hidden"
        aria-label="Toggle menu"
      >
        <LuMenu size={19} />
      </button>

      <h1 className="min-w-0 truncate text-[15px] font-semibold text-white sm:text-base">
        {pageTitle}
      </h1>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-3">

        <button
          type="button"
          onClick={() => setMobileSearchOpen((v) => !v)}
          className="rounded-md p-2 text-slate-400 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white md:hidden"
          aria-label="Search"
        >
          {mobileSearchOpen ? <LuX size={17} /> : <LuSearch size={17} />}
        </button>

        <button
          type="button"
          className="relative rounded-md p-2 text-slate-400 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
          aria-label="Notifications"
        >
          <LuBell size={17} />
          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-[#5B7CFF] shadow-[0_0_6px_rgba(91,124,255,0.8)]" />
        </button>

        <span className="hidden h-6 w-px bg-white/10 sm:block" />

        <div className="relative" ref={profileRef}>
          <button
            type="button"
            onClick={() => setProfileOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md py-1.5 pl-1.5 pr-2 transition-colors duration-200 hover:bg-white/[0.06]"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#5B7CFF]/15 text-[11px] font-semibold text-[#AAB8FF] ring-1 ring-[#5B7CFF]/25">
              {initials}
            </span>
            <span className="hidden max-w-[110px] truncate text-[13px] font-medium text-slate-200 sm:inline">
              {user?.name || "Account"}
            </span>
            <LuChevronDown
              size={14}
              className={`hidden text-slate-500 transition-transform duration-200 sm:inline ${
                profileOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {profileOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.97 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-2 w-48 overflow-hidden rounded-md border border-white/10 bg-[#0D1424] shadow-xl"
              >
                <div className="border-b border-white/[0.06] px-3.5 py-3">
                  <p className="truncate text-[13px] font-medium text-white">
                    {user?.name || "Account"}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {user?.email || ""}
                  </p>
                </div>
                <button className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] text-slate-300 transition-colors duration-200 hover:bg-white/[0.05]">
                  <LuUser size={14} /><Link to={PROFILE}> Profile</Link>
                </button>
                <button
                  onClick={() => dispatch(logoutUser())}
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] text-red-400 transition-colors duration-200 hover:bg-red-500/10"
                >
                  <LuLogOut size={14} /> Sign out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {mobileSearchOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute left-0 right-0 top-16 p-3 md:hidden"
            style={{
              background: "#0D1330",
              borderBottom: "1px solid rgba(91,124,255,0.15)",
            }}
          >
            {/* <div className="relative">
              <LuSearch
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                autoFocus
                type="text"
                placeholder="Search inventory..."
                className="w-full rounded-md border border-white/10 bg-white/[0.03] py-2.5 pl-9 pr-3 text-[13px] text-slate-200 placeholder:text-slate-500 outline-none focus:border-[#5B7CFF]/40"
              />
            </div> */}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
